import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';


const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const SURVEY_END_DATE = '2025-11-01';

const BannerModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    localStorage.setItem('surveyModalClosed', 'true');
    localStorage.setItem('surveyModalClosedTime', Date.now().toString());
  };

  const shouldShowModal = useCallback((): boolean => {
    const modalClosed = localStorage.getItem('surveyModalClosed');
    const modalClosedTime = localStorage.getItem('surveyModalClosedTime');

    return !modalClosed ||
      (modalClosedTime && Date.now() - parseInt(modalClosedTime) > TWENTY_FOUR_HOURS);
  }, []);

  const isSurveyPeriodActive = useCallback((): boolean => {
    return new Date() < new Date(SURVEY_END_DATE);
  }, []);

  const isKoreanLanguage = useCallback((): boolean => {
    const browserLanguage = navigator.language || navigator.languages?.[0];
    return browserLanguage?.startsWith('ko') || false;
  }, []);

  const isKoreanIP = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return data.country_code === 'KR';
    } catch (error) {
      return false;
    }
  }, []);

  const checkKoreanUser = useCallback(async (): Promise<boolean> => {
    if (!isSurveyPeriodActive()) return false;

    if (isKoreanLanguage()) return true;

    return await isKoreanIP();
  }, [isSurveyPeriodActive, isKoreanLanguage, isKoreanIP]);

  useEffect(() => {
    (async () => {
      // 먼저 모달 표시 조건 체크 (localStorage 확인)
      if (!shouldShowModal()) {
        return; // 모달을 표시할 필요가 없으면 한국인 체크도 생략
      }

      const isKorean = await checkKoreanUser();
      if (isKorean) {
        setIsOpen(true);
      }
    })();

  }, [checkKoreanUser, shouldShowModal]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // 스크롤 방지
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset'; // 스크롤 복원
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <ContentFrame>
            <TextContent>
              <MainText>
                우주항공청 SBIR 1단계 과제의 일환으로,<br />
                한국형 우주교통관리시스템(K-STM)에 대한 의견을 수집하고 있습니다.<br />
                우주분야 관계자 분들의 많은 참여를 기대합니다.
              </MainText>
              <RewardText>
                설문을 끝까지 응답해 주신 분들께는<br /> 스타벅스 기프티콘을 드릴 예정입니다. (선착순 100명)
              </RewardText>
            </TextContent>
            <SurveyInfo>
              <SurveyText>
                설문사이트 : <SurveyLink href="https://forms.gle/hrqRofeAJs8DHuze9" target="_blank" rel="noopener noreferrer">https://forms.gle/hrqRofeAJs8DHuze9</SurveyLink><br />
                조사기간 : 2025년 10월 31일 까지
              </SurveyText>
            </SurveyInfo>
          </ContentFrame>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );

  // Portal을 사용해서 body에 직접 렌더링
  return createPortal(modalContent, document.body);
};

export default BannerModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  position: relative;
  width: 491px;
  height: 368px;
  background: #292929;
  border: 1px solid #4b4b4b;
  border-radius: 8px;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 13px 8px;
  gap: 306px;
  isolation: isolate;
  width: 491px;
  height: 48px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  width: 28px;
  height: 28px;
  right: 10px;
  top: 10px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
`;

const CloseIcon = styled.div`
  position: absolute;
  left: 20.83%;
  right: 20.83%;
  top: 20.83%;
  bottom: 20.83%;
  background: #949494;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E") no-repeat center;
  mask-size: contain;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 32px 32px;
  gap: 24px;
  width: 491px;
  height: 320px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 1;
`;

const ContentFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: 427px;
  height: 152px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 1;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: 427px;
  height: 120px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 1;
`;

const MainText = styled.div`
  width: 405px;
  height: 60px;
  font-family: 'Noto Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  text-align: center;
  letter-spacing: -0.005em;
  color: #ffffff;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const RewardText = styled.div`
  width: 220px;
  height: 60px;
  font-family: 'Noto Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  text-align: center;
  letter-spacing: -0.005em;
  color: #ffffff;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const SurveyInfo = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 32px 0px;
  gap: 8px;
  width: 427px;
  height: 104px;
  border: 1px solid #4b4b4b;
  border-radius: 4px;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
`;

const SurveyText = styled.div`
  width: 326px;
  height: 40px;
  font-family: 'Noto Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: -0.005em;
  color: #ffffff;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const SurveyLink = styled.a`
  color: #ffffff;
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: #cccccc;
  }
  
  &:visited {
    color: #ffffff;
  }
`;
